import { createAsyncThunk } from '@reduxjs/toolkit';
import { getTranslationKey } from 'helpers/texting';
import { getSelectionIdFromUrl } from 'helpers/selectionUrl';
import { Api } from 'old-store/utils';
import { OrderStatus, PaymentMethod } from 'constants/payment';
import { setCollection } from 'store/slices/collection';
import { fetchSelectionImages, setImages } from 'store/slices/images';
import { saveSelection } from 'store/slices/selection';
import { hydrateNewSelection } from 'modules/ScrAppBookNew/store/selection';
import { showGlobalError } from '../error';
import { getProductByID, getProductBySlug } from '../products/selectors';
import { cloneDeep, find, get, keyBy, omit } from 'lodash';
import {
  getHorizontalImages,
  getSelectedImagesIDsSelector,
  getShopCroppedImagesSelector,
  getVerticalImages,
  otpLoginFlowSelector
} from './selectors';
import { RootState } from 'old-store/store';
import {
  IAddImageAction,
  IAddImagesAction,
  ICreateAccountWithPasswordParams,
  ILoginWithPasswordParams,
  ISetShopEditMode,
  ISignedInCustomer,
  IStartOtpLoginResult,
  IVerifyLoginCodeResult,
  OtpLoginStep
} from './types';
import {
  fetchAllLaboratoriesActionSuccess,
  fetchGlobalShopSettingsForCollectionActionSuccess,
  fetchLaboratoryActionSuccess,
  fetchProductBestsellerListSuccess,
  fetchProductRecListSuccess,
  setCropImagesAction,
  setGroupedByGalleriesImages,
  setGroupedImages,
  setSelectedImagesSuccessAction,
  updateShopDataAction
} from '.';
import { getCollectionById, trackEventAction } from 'old-store/actions';
import {
  deleteCroppedImageSuccessAction,
  resetLoginWithPasswordFlow,
  resetOtpLoginFlow,
  resetSignupWithPasswordFlow,
  setCreateAccountWithPasswordFlowStatus,
  setOtpLoginFlowStatus,
  setEndCustomerEmailWasNotFound,
  setEndCustomerHasPassword,
  setForgotPasswordFlowStatus,
  setLoginFlowStatus,
  toggleEnterPasswordModalAction,
  toggleLoginWithPasswordModalAction
} from 'modules/ScrShopNew/store/slices/shop';
import {
  claimCheckoutAction,
  fetchCheckoutAction,
  pruneUnavailableCartItems,
  recalculatePricingAction
} from 'modules/ScrShopNew/store/slices/checkout/actions';
import { fetchProducts } from 'modules/ScrShopNew/store/slices/products/actions';
import { getCheckoutIDSelector } from 'modules/ScrShopNew/store/slices/checkout/selectors';
import { getPaymentMethod } from 'modules/ScrShopNew/store/slices/shop/selectors';
import { globalError } from 'modules/ScrShopNew/store/slices/error';
import { getGalleries } from 'modules/ScrShopNew/store/slices/galleries/selectors';
import { getImagesByGalleries, getShuffledImages } from 'modules/ScrShopNew/store/utils/helpers';
import { getCheckoutProductByID } from '../checkout/selectors';
import ApiErrors from 'old-store/utils/API/APIErrors';
import { getEndCustomerData } from 'old-store/selectors/endCustomer';
import { saveEndCustomer } from 'store/slices/endCustomer';
import { fetchMessage } from 'store/slices/messages/actions';
import TagManager from 'react-gtm-module';
import db from 'database';
import { saveAndSetNewToken } from 'helpers/tokens';
import { getErrorStatus, handleRequestError } from 'helpers/network';
import { toast } from 'react-toastify';
import { updateCheckoutSummaryUIAction, updateCheckoutStoreAction } from '../checkout';
import { convertToOrderSuccessAction } from '../orders';

export const getIsAdding = (max: number, selected: string[], imageID: string) =>
  max === 1 && selected.indexOf(imageID) === -1;
export const getIsRemoveImage = (max: number, selected: string[], imageID: string) =>
  selected.indexOf(imageID) > -1 && max !== 1;

export const updateSelection = createAsyncThunk(
  'selection/update',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const usesNewGallery = state.collection.collectionCreator?.featureFlags?.newDesign === true;
      // a sign-in on a linked selection's page must keep it on screen (new design's Edit flow)
      const { selections } = await Api.Auth.updateEndCustomer(
        usesNewGallery ? getSelectionIdFromUrl() : undefined
      );
      const payload = { selections, allUserSelections: [] };

      dispatch(usesNewGallery ? hydrateNewSelection(payload) : saveSelection(payload));
      dispatch(fetchSelectionImages(selections));
      dispatch({ type: 'fetch_selections', payload: selections });
      dispatch({ type: 'set_selection_fetch_status', payload: 'success' });

      const res = await Api.Site.get();
      dispatch(setCollection(res.result));

      return selections;
    } catch (error) {
      // @ts-ignore
      return rejectWithValue(error.response?.data);
    }
  }
);

export const setSelectedImage = createAsyncThunk(
  'image/setSelected',
  async (
    { imageID, productSlug, groupSlug, callback }: IAddImageAction,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const selected = cloneDeep(getSelectedImagesIDsSelector(state));
      const croppedImages = getShopCroppedImagesSelector(state);
      const product =
        groupSlug === 'digital'
          ? getProductByID(state)(productSlug)
          : getProductBySlug(state)(productSlug);

      // @ts-ignore
      const { maxImageSelection } = product;
      const isRemove = getIsRemoveImage(maxImageSelection, selected, imageID);

      if (!isRemove && maxImageSelection === selected.length && maxImageSelection !== 1) {
        dispatch(
          showGlobalError({
            title: getTranslationKey('shop.imagesSelectionModal.title'),
            text: getTranslationKey('shop.imagesSelectionModal.toManyImagesText'),
            confirmText: getTranslationKey('shop.imagesSelectionModal.button').toUpperCase()
          })
        );

        return rejectWithValue('Maximum selection limit reached');
      }

      let IDs = selected;
      if (getIsRemoveImage(maxImageSelection, selected, imageID)) {
        IDs = selected.filter((id) => id !== imageID);
        // @ts-ignore
        dispatch(setCropImagesAction(omit(croppedImages, imageID)));
      } else if (getIsAdding(maxImageSelection, selected, imageID)) {
        IDs = [imageID];
      } else if (!maxImageSelection || maxImageSelection > 1) {
        IDs.push(imageID);
      }

      dispatch(setSelectedImagesSuccessAction([...IDs]));
      callback?.();
      dispatch(
        trackEventAction({
          name: 'images-selected',
          payload: {
            images_ids: [imageID],
            images_count: 1,
            product_group_slug: groupSlug,
            product_slug: productSlug
          }
        })
      );
    } catch (error) {
      // @ts-ignore
      return rejectWithValue(error.message);
    }
  }
);

export const setSelectedImagesAction = createAsyncThunk(
  'images/setSelected',
  async (payload: IAddImagesAction, { getState, dispatch }) => {
    const state = getState() as RootState;
    const selectedItem = getSelectedImagesIDsSelector(state);
    const selected = [...selectedItem]; // Cloning for immutability
    const productDetails =
      payload.groupSlug === 'digital'
        ? getProductByID(state)(payload.productSlug)
        : getProductBySlug(state)(payload.productSlug);

    // @ts-ignore
    const { maxImageSelection } = productDetails;

    const imagesThatNotSelected = payload.imageIDs.filter((imageId) => !selected.includes(imageId));
    const allImagesSelected = payload.imageIDs.every((imageId) => selected.includes(imageId));

    if (imagesThatNotSelected.length > 0) {
      if (maxImageSelection === selected.length && maxImageSelection !== 1) {
        dispatch(
          showGlobalError({
            title: getTranslationKey('shop.imagesSelectionModal.title'),
            text: getTranslationKey('shop.imagesSelectionModal.tooManyImagesText'),
            confirmText: getTranslationKey('shop.imagesSelectionModal.button').toUpperCase()
          })
        );

        return;
      }

      const imagesToAdd =
        typeof maxImageSelection === 'number'
          ? imagesThatNotSelected.slice(0, maxImageSelection - selected.length)
          : imagesThatNotSelected;

      selected.push(...imagesToAdd);
    }

    // Removing deselected images
    if (allImagesSelected) {
      payload.imageIDs.forEach((imageId) => {
        const index = selected.indexOf(imageId);

        if (index > -1) {
          selected.splice(index, 1);
        }
      });
    }

    dispatch(setSelectedImagesSuccessAction([...selected]));
    dispatch(
      trackEventAction({
        name: 'images-selected',
        payload: {
          images_ids: payload.imageIDs,
          images_count: payload.imageIDs.length,
          product_group_slug: payload.groupSlug,
          product_slug: payload.productSlug
        }
      })
    );
  }
);

export const deleteCroppedImage = createAsyncThunk(
  'images/deleteCropped',
  async (payload: { _id: string; goBackCallback: () => void }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const selectedImagesIDs = getSelectedImagesIDsSelector(state);
    const croppedImages = getShopCroppedImagesSelector(state);

    const filteredSelected = selectedImagesIDs.filter((id) => id !== payload._id);
    const filteredCropped = omit(croppedImages, payload._id);

    dispatch(setSelectedImagesSuccessAction(filteredSelected));
    // @ts-ignore
    dispatch(deleteCroppedImageSuccessAction(filteredCropped));

    if (!filteredSelected.length) {
      payload?.goBackCallback();
    }
  }
);

export const setShopEditMode = createAsyncThunk(
  'shop/setEditMode',
  async (payload: ISetShopEditMode, { getState, dispatch }) => {
    const state = getState() as RootState;
    const checkoutProduct = getCheckoutProductByID(state)(payload.cartSelectedProductID);
    const { options } = getProductByID(state)(checkoutProduct?._product) || { options: [] };
    const images = [...get(checkoutProduct, 'images', [])].sort((a, b) => a.order - b.order);
    const convertImages = images.map((image) => ({
      ...image.crop,
      quantity: image.quantity,
      _id: image._id,
      _image: `${checkoutProduct?.slug}-${image._image}`
    }));

    const currentOptions = {
      border: '',
      finish: '',
      color: '',
      surface: '',
      material: '',
      'border-color': '',
      'color-combination': ''
    };

    checkoutProduct.selectedOptions.forEach((option) => {
      const productOption = find(options, { _id: option });

      if (productOption) {
        currentOptions[productOption.type] = option;
      }
    });

    dispatch(setSelectedImagesSuccessAction(images.map((image) => image._image)));
    dispatch(
      updateShopDataAction({
        options: currentOptions,
        croppedImages: keyBy(convertImages, '_image'),
        cartSelectedProductID: payload.cartSelectedProductID,
        selectedProductID: payload.productID,
        isEditMode: true
      })
    );
  }
);

export const fetchGlobalShopSettingsForCollection = createAsyncThunk(
  'shop/fetchGlobalShopSettingsForCollection',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await Api.Shop.getGlobalShopSettingsForCollection(window.SITE_ID);

      ApiErrors.checkOnApiError(response);
      dispatch(fetchGlobalShopSettingsForCollectionActionSuccess(response.result));
    } catch (err) {
      // @ts-ignore
      if (err.code !== 404) {
        console.error(err);
      }

      return rejectWithValue(err);
    }
  }
);

export const checkEndCustomerPassword = createAsyncThunk(
  'endCustomer/checkPassword',
  async (payload: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await Api.Auth.checkPassword(payload, window.SITE_ID);
      ApiErrors.checkOnApiError(response);
      dispatch(setEndCustomerHasPassword(response.result.endCustomerHasPassword));
      dispatch(toggleLoginWithPasswordModalAction());
      dispatch(toggleEnterPasswordModalAction());
    } catch (err) {
      // @ts-ignore
      if (err.code !== 404) {
        console.error(err);
      } else {
        dispatch(setEndCustomerEmailWasNotFound(true));
      }

      return rejectWithValue(err);
    }
  }
);

export const checkEndCustomerEmail = createAsyncThunk(
  'endCustomer/checkEmail',
  async (payload: string, { dispatch }) => {
    try {
      const response = await Api.Auth.checkEmail(payload, window.SITE_ID);
      ApiErrors.checkOnApiError(response);

      if (response.result.emailIsAlreadyTaken && !response.result.endCustomerHasPassword) {
        dispatch(
          setCreateAccountWithPasswordFlowStatus({
            showEmailModal: false
          })
        );
        dispatch(
          setLoginFlowStatus({
            email: payload,
            showEnterPasswordModal: true,
            endCustomerHasPassword: false
          })
        );
      } else {
        dispatch(
          setCreateAccountWithPasswordFlowStatus({
            showEmailModal:
              response.result.emailIsAlreadyTaken && response.result.endCustomerHasPassword,
            showPasswordModal: !response.result.emailIsAlreadyTaken,
            emailIsAlreadyTaken:
              response.result.emailIsAlreadyTaken && response.result.endCustomerHasPassword
          })
        );
      }
    } catch (err) {
      console.error(err);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'endCustomer/resetPassword',
  async (payload: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await Api.Auth.resetPassword(payload, window.SITE_ID);
      ApiErrors.checkOnApiError(response);
      dispatch(
        setForgotPasswordFlowStatus({
          showSuccessMessage: true
        })
      );
    } catch (err) {
      // @ts-ignore
      if (err.code === 404) {
        dispatch(
          setForgotPasswordFlowStatus({
            emailDoesNotExists: true
          })
        );
      } else {
        console.error(err);
      }

      return rejectWithValue(err);
    }
  }
);

export const fetchProductRecList = createAsyncThunk(
  'shop/fetchProductRecList',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await Api.Shop.getRecommendations();
      ApiErrors.checkOnApiError(response);
      dispatch(fetchProductRecListSuccess(response.result));
    } catch (e) {
      console.error(e);

      return rejectWithValue(e);
    }
  }
);

export const fetchProductBestsellerList = createAsyncThunk(
  'shop/fetchProductBestsellerList',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await Api.Shop.getBestsellers();
      ApiErrors.checkOnApiError(response);
      dispatch(fetchProductBestsellerListSuccess(response.result));
    } catch (e) {
      console.error(e);

      return rejectWithValue(e);
    }
  }
);

const deleteSiteCache = async () => {
  // @ts-ignore
  await db.sites.delete(window.SITE_ID).catch((e) => {
    if (e && e.inner) console.log('DataBaseError helper deleteCache inner:', e.inner, e);
    else console.log('DataBaseError helper deleteCache:', e);
  });
};

interface IFinishCustomerLoginParams {
  endCustomerToken: string;
  endCustomer: any;
  // the end customer as it was before signing in (a fly user keeps its id for tracking)
  previousEndCustomer: { _id?: string; type?: string };
  trackEventName: 'user-signed-in' | 'user-signed-up';
}

// Everything after a successful sign-in (password or one-time code): persist the token, sync the
// selection / end customer, restore or claim the cart and refetch the customer-bound data.
const finishCustomerLogin = async (
  dispatch: any,
  getState: () => unknown,
  { endCustomerToken, endCustomer, previousEndCustomer, trackEventName }: IFinishCustomerLoginParams
) => {
  const state = getState();
  const { _id, type } = previousEndCustomer;
  // @ts-ignore
  const { signedWildCardUrl, _user } = state.collection; // Assuming this exists, might need to adjust based on actual state structure

  saveAndSetNewToken(_user, endCustomerToken);
  dispatch({ type: 'fetch_endCustomer', payload: endCustomer });
  dispatch(
    trackEventAction({
      name: trackEventName,
      payload: { endCustomer, flyUserId: type === 'fly' && _id }
    })
  );

  await dispatch(updateSelection());

  dispatch(resetLoginWithPasswordFlow());
  dispatch(resetOtpLoginFlow());
  dispatch(fetchProductBestsellerList());
  dispatch(fetchProductRecList());
  dispatch(saveEndCustomer({ ...endCustomer, _id: endCustomer.id, authed: true }));
  dispatch(fetchMessage());

  const latestPayment = await Api.Orders.reFetchLatestPayment({
    collection_id: window.SITE_ID
  });

  if (latestPayment._order) {
    dispatch(updateCheckoutSummaryUIAction({ ...latestPayment._order }));
  }

  const hasPendingPayment = latestPayment?._order?.status === OrderStatus.PAYMENT_PENDING;
  const paymentMethod = getPaymentMethod(getState() as RootState);
  const isPaypalPendingPayment = hasPendingPayment && paymentMethod === PaymentMethod.PAYPAL;

  if (isPaypalPendingPayment) {
    // PayPal needs to complete the pending payment — restore order and redirect
    dispatch(
      convertToOrderSuccessAction({
        ...latestPayment._order,
        payments: [latestPayment]
      })
    );
    dispatch(
      updateCheckoutStoreAction({
        products: latestPayment._order.products,
        _id: latestPayment._order._shoppingCart,
        currency: latestPayment._order.currency
      })
    );
  } else {
    // No pending payment or non-PayPal — start fresh, claim and fetch active cart.
    // Refresh products first so the cart prune sees up-to-date availability
    // (e.g. VAT-ID removed → no digital products for this customer).
    await dispatch(fetchProducts({ collection_id: window.SITE_ID }));
    await dispatch(claimCheckoutAction());
    await dispatch(fetchCheckoutAction());

    const updatedState = getState() as RootState;
    const checkoutId = getCheckoutIDSelector(updatedState);
    if (checkoutId) {
      await dispatch(pruneUnavailableCartItems());
      await dispatch(recalculatePricingAction({ cartId: checkoutId }));
    }
  }

  TagManager.dataLayer({
    dataLayer: { event: 'login' },
    dataLayerName: window.SHOP_DATA_LAYER_NAME
  });

  // Refetch images to synchronize user id
  const images = await Api.Images.getImages();
  dispatch(setImages({ images, signedWildCardUrl }));
  dispatch(getCollectionById(window.SITE_ID));
};

export const loginCustomerWithPassword = createAsyncThunk(
  'endCustomer/loginWithPassword',
  async (
    payload: ILoginWithPasswordParams,
    { dispatch, getState }
  ): Promise<ISignedInCustomer | undefined> => {
    const formValues = {
      collectionId: window.SITE_ID,
      ...payload
    };

    try {
      // Delete cache before login
      await deleteSiteCache();

      dispatch(setLoginFlowStatus({ passwordIsInvalid: false }));

      const previousEndCustomer = getEndCustomerData(getState());
      const response = await Api.Auth.login(formValues);
      ApiErrors.checkOnApiError(response);

      const { endCustomerToken, endCustomer } = response.result;

      await finishCustomerLogin(dispatch, getState, {
        endCustomerToken,
        endCustomer,
        previousEndCustomer,
        trackEventName: 'user-signed-in'
      });

      return { endCustomer };
    } catch (e) {
      // @ts-ignore
      if (e.code === 403) {
        dispatch(setLoginFlowStatus({ passwordIsInvalid: true }));
      } else {
        dispatch(globalError(e as any));
      }
    }
  }
);

export const updateEndCustomer = createAsyncThunk(
  'endCustomer/update',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await Api.Auth.updateEndCustomer();
      dispatch({ type: 'fetch_endCustomer', payload: res });
    } catch (e) {
      console.error(e);

      return rejectWithValue(e);
    }
  }
);

export const createAccountWithPassword = createAsyncThunk(
  'endCustomer/createAccountWithPassword',
  async (payload: ICreateAccountWithPasswordParams, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const { _id, type } = getEndCustomerData(state);
      const response = await Api.Auth.signup(payload);
      ApiErrors.checkOnApiError(response);
      const { endCustomerToken, endCustomer } = response.result;
      // @ts-ignore
      const { _user, signedWildCardUrl } = state.collection;
      saveAndSetNewToken(_user, endCustomerToken);

      // 'updateEndCustomer' has to be written with 'createAsyncThunk' to make it work and return promise
      await dispatch(updateEndCustomer());
      dispatch(claimCheckoutAction());
      dispatch(resetSignupWithPasswordFlow());
      dispatch(
        trackEventAction({
          name: 'user-signed-up',
          payload: { endCustomer, flyUserId: type === 'fly' && _id }
        })
      );
      dispatch(fetchProductBestsellerList());
      dispatch(fetchProductRecList());
      dispatch(
        saveEndCustomer({
          ...endCustomer,
          _id: endCustomer.id,
          authed: true
        })
      );

      // Recalculate pricing after signup to update prices based on user's information
      const checkoutId = getCheckoutIDSelector(state as RootState);
      if (checkoutId) {
        dispatch(recalculatePricingAction({ cartId: checkoutId }));
      }

      // Refetch images to synchronize user id, assuming Api.Images.getImages exists and is correctly referenced
      const images = await Api.Images.getImages();
      dispatch(setImages({ images, signedWildCardUrl }));
    } catch (e) {
      console.error(e);

      return rejectWithValue(e);
    }
  }
);

// status codes the BE is expected to answer a wrong / expired code with
const INVALID_CODE_STATUSES = [400, 401, 410];

const getOtpEmail = (getState: () => unknown) =>
  otpLoginFlowSelector(getState() as RootState).email;

// An account with a password signs in with it first (screen 3); the code is the alternative.
const getStepAfterEmail = ({ accountExists, hasPassword }: IStartOtpLoginResult): OtpLoginStep => {
  if (!accountExists) return 'registerCode';

  return hasPassword ? 'password' : 'code';
};

const handleOtpRequestError = (e: unknown) => {
  if (getErrorStatus(e) === 429) {
    toast.error(getTranslationKey('tooManyAttemptsMessage'), {
      theme: 'colored',
      hideProgressBar: true,
      toastId: 'otp-too-many-attempts'
    });

    return;
  }

  // 503 (the email couldn't be sent) etc.
  handleRequestError(e, { showFallback: true });
};

// Email screen → the BE emails a code unless the account has a password; that one asks for
// a code through `sendOtpLoginCode` only when the customer picks "Sign in with code".
export const startOtpLogin = createAsyncThunk(
  'endCustomer/startOtpLogin',
  async (email: string, { dispatch, getState, rejectWithValue }) => {
    const normalizedEmail = email.trim().toLowerCase();
    // "Resend the code" goes through here too and must keep the customer on the code screen
    const currentStep = otpLoginFlowSelector(getState() as RootState).step;
    const isResend = currentStep === 'code' || currentStep === 'registerCode';

    dispatch(setOtpLoginFlowStatus({ email: normalizedEmail, isLoading: true }));

    try {
      const response = await Api.Auth.startOtpLogin(
        normalizedEmail,
        window.SITE_ID,
        window.LANGUAGE
      );
      ApiErrors.checkOnApiError(response);
      const { result } = response;

      dispatch(
        setOtpLoginFlowStatus({
          step: isResend ? currentStep : getStepAfterEmail(result),
          hasPassword: result.hasPassword,
          codeIsInvalid: false
        })
      );
    } catch (e) {
      handleOtpRequestError(e);

      return rejectWithValue(e);
    } finally {
      dispatch(setOtpLoginFlowStatus({ isLoading: false }));
    }
  }
);

// "Sign in with code" from the password screen, and a resend for an account with a password.
export const sendOtpLoginCode = createAsyncThunk(
  'endCustomer/sendOtpLoginCode',
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    dispatch(setOtpLoginFlowStatus({ isLoading: true }));

    try {
      const response = await Api.Auth.sendOtpLoginCode(getOtpEmail(getState), window.SITE_ID);
      ApiErrors.checkOnApiError(response);

      dispatch(setOtpLoginFlowStatus({ step: 'code', codeIsInvalid: false }));
    } catch (e) {
      handleOtpRequestError(e);

      return rejectWithValue(e);
    } finally {
      dispatch(setOtpLoginFlowStatus({ isLoading: false }));
    }
  }
);

// Login / registration code screens: the BE creates the account when there is none yet.
export const verifyLoginCode = createAsyncThunk(
  'endCustomer/verifyLoginCode',
  async (code: string, { dispatch, getState, rejectWithValue }) => {
    const email = getOtpEmail(getState);

    dispatch(setOtpLoginFlowStatus({ isLoading: true, codeIsInvalid: false }));

    try {
      await deleteSiteCache();

      const previousEndCustomer = getEndCustomerData(getState());
      const response = await Api.Auth.verifyLoginCode({
        email,
        code: code.trim(),
        collectionId: window.SITE_ID,
        language: window.LANGUAGE
      });
      ApiErrors.checkOnApiError(response);

      const { endCustomerToken, endCustomer, isNewAccount }: IVerifyLoginCodeResult =
        response.result;

      if (isNewAccount) {
        // 'updateEndCustomer' has to be awaited, as in createAccountWithPassword
        await dispatch(updateEndCustomer());
      }

      await finishCustomerLogin(dispatch, getState, {
        endCustomerToken,
        endCustomer,
        previousEndCustomer,
        trackEventName: isNewAccount ? 'user-signed-up' : 'user-signed-in'
      });

      return { endCustomer } as ISignedInCustomer;
    } catch (e) {
      // @ts-ignore
      if (INVALID_CODE_STATUSES.includes(e.code)) {
        dispatch(setOtpLoginFlowStatus({ codeIsInvalid: true }));
      } else {
        // 409 (the account was just created by a parallel request), 503, 429 etc.
        handleRequestError(e, { showFallback: true });
      }

      return rejectWithValue(e);
    } finally {
      dispatch(setOtpLoginFlowStatus({ isLoading: false }));
    }
  }
);

// "Forgot password" → emails a new password to the address from the email screen.
export const requestNewPassword = createAsyncThunk(
  'endCustomer/requestNewPassword',
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(setOtpLoginFlowStatus({ isLoading: true }));

    try {
      const response = await Api.Auth.resetPassword(getOtpEmail(getState), window.SITE_ID);
      ApiErrors.checkOnApiError(response);

      dispatch(setOtpLoginFlowStatus({ step: 'newPassword' }));
    } catch (e) {
      dispatch(globalError(e as any));

      return rejectWithValue(e);
    } finally {
      dispatch(setOtpLoginFlowStatus({ isLoading: false }));
    }
  }
);

export const fetchLaboratory = createAsyncThunk(
  'shop/fetchLaboratory',
  async (payload: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await Api.Shop.getLaboratory(payload);
      ApiErrors.checkOnApiError(response);

      if (Array.isArray(response.result?.list)) {
        dispatch(fetchAllLaboratoriesActionSuccess(response.result.list));
      } else {
        dispatch(fetchLaboratoryActionSuccess(response.result));
      }
    } catch (e) {
      console.error(e);

      return rejectWithValue(e);
    }
  }
);

export const groupImagesBySizeType = createAsyncThunk(
  'images/setGroupedImages',
  (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const galleries = getGalleries(state);
    const horizontal = getHorizontalImages(state) as any[];
    const vertical = getVerticalImages(state) as any[];

    const horizontalImages = getShuffledImages(horizontal);
    const verticalImages = getShuffledImages(vertical);
    const fallbackImages = getShuffledImages([...horizontal, ...vertical]);

    dispatch(
      setGroupedImages({
        horizontal: horizontalImages,
        vertical: verticalImages,
        fallback: fallbackImages
      })
    );

    dispatch(
      setGroupedByGalleriesImages({
        horizontal: getImagesByGalleries(horizontalImages, galleries),
        vertical: getImagesByGalleries(verticalImages, galleries),
        fallback: getImagesByGalleries(fallbackImages, galleries)
      })
    );
  }
);
